import {getUserData} from './API';
import {Storage} from 'aws-amplify';
import {uuidToHEX} from './util';
import device from './device/device.js';
import stringify from 'json-stringify-safe';
import { interventionType } from '../constants/interventionSelectionConstants.js';
import { FEATURE_FLAGS } from '../constants/featureFlags.js';
import crypto from 'crypto';

let utteranceLogs = [];

export function logSessionInfo(inType,inData,forceKey=null){
  let sessionInfo = getUserData();
  if(!sessionInfo && !forceKey) return;

  let activity = forceKey ? forceKey : sessionInfo.activityId;
  if(!activity){
    if(sessionInfo.userId){
      activity = uuidToHEX(getUserData().userId);
    }
  }

  if(activity){
    const options = {
      bucket: process.env.LOGGINGBUCKET + "/" + activity,
      contentType: 'application/json',
      level: "public",
      customPrefix: {
        public: "",
      },
    };

    return Storage.put(inType+"_"+Date.now()+".json",stringify(inData),options)
    .then(()=>{
      return Promise.resolve(true);
    })
    .catch(err => {
      console.log("LOGGING PUT FAIL:",err);
      return Promise.resolve(false);
    });
  }
}

export function logSystemData(){
  logSessionInfo("browser",{browser: getUserData().browser,platform: navigator.platform,touchPoints: navigator.maxTouchPoints});
  logSessionInfo("network",getUserData().connection);
  logSessionInfo("device",device);

  //Remove the sessionData, which has PII and can get quite large over time
  let sessionInfo = JSON.parse(JSON.stringify(getUserData()));
  delete sessionInfo.sessionData;

  logSessionInfo("session",sessionInfo);
}

function getFormattedUTCDate() {
  const date = new Date(); // This gets the current date and time
  const year = date.getUTCFullYear(); // Gets the year (4 digits)
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Gets the month, adds 1, and pads if necessary
  const day = String(date.getUTCDate()).padStart(2, '0'); // Gets the day of the month and pads if necessary

  return `${year}-${month}-${day}`; // Formats and returns the date as YYYY-MM-DD
}

export function logInterventionData(features, selection) { 
  const scope = features.interactions.scope;
  const { phraseIndex, words } = features.story.textMetadataForCurrentPhrase;
  const {
    index: wordIndex,
    word,
    type,
  }  = selection;

  if (scope === 'phrase' && (word === null || word === undefined) && type === interventionType.none) {
    // Do not save intervention data if no valid errors at end of phrase
    return Promise.resolve();
  }

  // Do not save intervention data if all interventions in phraseInterventionWeights are found in the history for the given word
  if (
    scope === 'phrase' &&
    wordIndex !== undefined &&
    wordIndex !== null &&
    selection.history &&
    selection.history.length > 0 &&
    selection.phraseInterventionWeights &&
    selection.phraseInterventionWeights.length > 0
  ) {
    const { phraseInterventionWeights, history } = selection;

    const interventionOptionsForWord = Object.keys(phraseInterventionWeights[wordIndex]);
    const historyInterventionTypesForWord = [];
    history.forEach((phraseInterventions, internalPhraseIndex) => {
      phraseInterventions.forEach((intervention) => {
        // Ignore interventions for other words
        if (intervention.word !== word) return;

        // Ignore the intervention that was just selected
        if (internalPhraseIndex === phraseIndex && intervention.type === type) return;

        historyInterventionTypesForWord.push(intervention.type);
      });
    });
    
    const intersection = interventionOptionsForWord.filter(type => historyInterventionTypesForWord.includes(type));
    
    if (intersection.length === interventionOptionsForWord.length) {
      return Promise.resolve();
    }
  }

  const options = {
    bucket: process.env.INTERVENTIONBUCKET + "/" + getFormattedUTCDate() + "/" + features.activityId,
    contentType: 'application/json',
    level: "public",
    customPrefix: {
      public: "",
    },
  };

  const locale = features.locale;
  const {
    isEarlyReader: isEnglishEarlyReader,
    isSpanishEarlyReader
  } = features.student;
  const isEarlyReader =
      (locale.toLowerCase() === "en-us" && isEnglishEarlyReader) ||
      (locale.toLowerCase() === "es-mx" && isSpanishEarlyReader);

  let isOverride = false;
  if (words && words.length > wordIndex) {
    const wordMetadata = words[wordIndex];
    if (wordMetadata && wordMetadata.IS_OVERRIDE) {
      isOverride = true;
    }
  }

  const eventName = `phrase_${phraseIndex}_scope_${scope}_locale_${locale}_erss_${isEarlyReader}_override_${isOverride}_word_${word}_selected_${type}`;

  const data = {
    wordIndex,
    word,
    type,
    history: selection.history,
    student: features.student,
    metadata: selection.metadata,
    interventionSelectionVersions: selection.versions,
    passSelected: selection.passSelected,
    log: selection.log,
    interactions: features.interactions,
    storyId: features.story.storyId,
    fallback: selection.fallback,
    closeError: selection.closeError,
  }

  return Storage.put(eventName, stringify(data), options)
    .then(()=>{
      return Promise.resolve(true);
    })
    .catch(err => {
      console.log("INTERVENTION LOGGING PUT FAIL:", err);
      return Promise.resolve(false);
    });
}

export function logCorruptedSpeechString(str) {
  const options = {
    bucket: process.env.LOGGINGBUCKET + "/corrupted_speech_strings",
    contentType: 'application/json',
    level: "public",
    customPrefix: {
      public: "",
    },
  };

  const data = {
    string: str
  }

  return Storage.put(crypto.createHash('md5').update(str).digest('hex') + ".json", stringify(data), options)
  .then(()=>{
    return Promise.resolve(true);
  })
  .catch(err => {
    console.log("LOGGING PUT FAIL:",err);
    return Promise.resolve(false);
  });
}

export function storeUtteranceLog(params) {
  if (!FEATURE_FLAGS.UTTERANCE_LOGS) {
    return;
  }

  utteranceLogs.push({
    ...params,
    timestamp: Date.now()
  });
}

export function getUtteranceLogs() {
  if (!FEATURE_FLAGS.UTTERANCE_LOGS) {
    return [];
  }

  return utteranceLogs;
}

export function clearUtteranceLogs() {
  utteranceLogs = [];
}